import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Empty, Spin, Alert, Tooltip, Select } from 'antd';
import {
  SendOutlined, HistoryOutlined, CloseOutlined, PushpinFilled, LockOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import { libellesTraduits } from '../../i18n/libelles';
import discussionService from '../../services/discussionService';
import SaisieMention from './SaisieMention';
import BulleMessage from './BulleMessage';
import CorpsMessage from './CorpsMessage';
import { can, HAB, getUser, CHARTE_COLORS as C } from '../../variables/constants';

/**
 * Fil de collaboration interne d'un dossier.
 *
 * TROIS DÉCISIONS STRUCTURENT CE COMPOSANT.
 *
 * 1. `LIRE NE MARQUE PAS`. Le serveur ne marque rien à la lecture : c'est le client
 *    qui avance le repère, et seulement pour ce qu'il a réellement affiché. C'est
 *    l'inverse du fil usager, où la lecture marque côté serveur - au prix d'un écran
 *    qu'on ne peut ni rafraîchir ni monter par anticipation sans éteindre le signal
 *    de non-lu. Ici le sondage est possible, précisément parce qu'il ne marque rien.
 *
 * 2. `LE FIL SE LIT DE HAUT EN BAS, L'API RÉPOND DE BAS EN HAUT`. La page arrive du
 *    plus récent au plus ancien ; on l'inverse à l'affichage et les pages suivantes
 *    s'empilent VERS LE HAUT, en compensant l'accroissement de hauteur - sans quoi
 *    l'agent est éjecté du passage qu'il lisait.
 *
 * 3. `LE BROUILLON SURVIT`. Conservé par dossier dans le stockage de session : on est
 *    interrompu vingt fois par jour, et perdre un message à moitié écrit est la
 *    première raison d'abandonner un outil de ce genre.
 */

const TAILLE_PAGE = 25;
const POLL_MS = 30000;

/** Sous ce seuil de distance au bas du fil, un nouveau message fait défiler. Au-delà, non : l'agent lit plus haut. */
const SEUIL_BAS_PX = 90;

const cleBrouillon = (entiteType, entiteId) => `discussion:brouillon:${entiteType}:${entiteId}`;

// Une clé par valeur de ANCRE (services/discussionService), dans l'ordre du filtre.

const LIBELLES_ANCRE = libellesTraduits('commun:filInterne.ancres');

export default function FilInterne({
  entiteType,
  entiteId,
  hauteur = 460,
  /** Ancre imposée par l'appelant - clic sur la pastille d'une victime, d'une offre… */
  ancre,
  onAncreChange,
  /** Message à mettre en évidence (lien profond d'un courriel ou de la cloche). */
  messageCible,
  /** Remonté à chaque rafraîchissement pour que le rail mette son badge à jour. */
  onResume,
  compact = false,
}) {
  const { t } = useTranslation('commun');
  const moi = getUser();
  const moiTrackingId = moi?.utilisateurTrackingId;

  const peutLire = can(HAB.DISCUSSION_READ);
  const peutEcrire = can(HAB.DISCUSSION_CREATE);

  const [messages, setMessages] = useState([]);   // du plus ancien au plus récent
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [chargement, setChargement] = useState(true);
  const [chargementAnciens, setChargementAnciens] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [resume, setResume] = useState(null);

  const [corps, setCorps] = useState(() => sessionStorage.getItem(cleBrouillon(entiteType, entiteId)) || '');
  const [envoi, setEnvoi] = useState(false);
  const [citation, setCitation] = useState(null);
  const [filtreAncre, setFiltreAncre] = useState(null);

  const zoneRef = useRef(null);
  const premierRendu = useRef(true);
  const dernierMarque = useRef(null);

  const ancreActive = ancre || null;

  // ── Brouillon ────────────────────────────────────────────────────────────
  useEffect(() => {
    const cle = cleBrouillon(entiteType, entiteId);
    if (corps) sessionStorage.setItem(cle, corps);
    else sessionStorage.removeItem(cle);
  }, [corps, entiteType, entiteId]);

  // ── Chargement ───────────────────────────────────────────────────────────
  const versLeBas = useCallback(() => {
    const zone = zoneRef.current;
    if (zone) zone.scrollTop = zone.scrollHeight;
  }, []);

  const estPresDuBas = () => {
    const zone = zoneRef.current;
    if (!zone) return true;
    return zone.scrollHeight - zone.scrollTop - zone.clientHeight < SEUIL_BAS_PX;
  };

  const charger = useCallback(async ({ silencieux = false } = {}) => {
    if (!peutLire) return;
    if (!silencieux) setChargement(true);
    try {
      const [fil, resumeFil] = await Promise.all([
        discussionService.messages(entiteType, entiteId, {
          page: 0,
          size: TAILLE_PAGE,
          ancreType: ancreActive?.type ?? filtreAncre ?? undefined,
          ancreTrackingId: ancreActive?.id ?? undefined,
        }),
        discussionService.resume(entiteType, entiteId),
      ]);
      setMessages([...fil.items].reverse());
      setTotal(fil.total);
      setPage(0);
      setResume(resumeFil);
      onResume?.(resumeFil);
      setErreur(null);
    } catch (err) {
      if (!silencieux) {
        setErreur(err?.response?.data?.message || t('filInterne.chargementImpossible'));
        setMessages([]);
      }
    } finally {
      if (!silencieux) setChargement(false);
    }
  }, [entiteType, entiteId, peutLire, ancreActive?.type, ancreActive?.id, filtreAncre, onResume, t]);

  useEffect(() => { charger(); }, [charger]);

  // Sondage : possible seulement parce que la lecture ne marque rien côté serveur.
  // Le défilement n'est forcé que si l'agent était déjà en bas - sinon un message
  // arrivant pendant qu'il relit un échange le projetterait ailleurs.
  useEffect(() => {
    if (!peutLire) return undefined;
    const id = setInterval(() => {
      const enBas = estPresDuBas();
      charger({ silencieux: true }).then(() => { if (enBas) requestAnimationFrame(versLeBas); });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [charger, peutLire, versLeBas]);

  // Ouverture sur le dernier message, une seule fois.
  useEffect(() => {
    if (!chargement && premierRendu.current && messages.length) {
      premierRendu.current = false;
      requestAnimationFrame(versLeBas);
    }
  }, [chargement, messages.length, versLeBas]);

  // ── Repère de lecture ────────────────────────────────────────────────────
  // Posé sur le dernier message affiché, et seulement quand l'onglet est au premier
  // plan : un panneau ouvert derrière une autre fenêtre n'a été lu par personne.
  //
  // `dernierMarque` évite de reposer le même repère à chaque re-rendu : la liste des
  // messages est remplacée à chaque sondage et à chaque réaction, ce qui déclencherait
  // sinon un appel réseau pour une information inchangée.
  useEffect(() => {
    if (chargement || !messages.length || document.hidden) return;
    const dernier = messages[messages.length - 1];
    if (dernierMarque.current === dernier.messageTrackingId) return;
    dernierMarque.current = dernier.messageTrackingId;
    discussionService.marquerLu(entiteType, entiteId, dernier.messageTrackingId)
      .then(() => setResume((r) => (r ? { ...r, nonLus: 0 } : r)))
      .catch(() => { dernierMarque.current = null; /* reposé au prochain rafraîchissement */ });
  }, [chargement, messages, entiteType, entiteId]);

  // ── Lien profond ─────────────────────────────────────────────────────────
  // Un courriel de mention pointe un message précis. On fait défiler jusqu'à lui dès
  // qu'il apparaît dans la liste - l'effet est rejoué à chaque chargement de page
  // ancienne, de sorte qu'un message enfoui se cadre dès que l'agent l'a chargé.
  useEffect(() => {
    if (!messageCible || chargement) return;
    const el = document.getElementById(`msg-${messageCible}`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [messageCible, chargement, messages]);

  const chargerAnciens = async () => {
    setChargementAnciens(true);
    try {
      const suivante = page + 1;
      const fil = await discussionService.messages(entiteType, entiteId, {
        page: suivante,
        size: TAILLE_PAGE,
        ancreType: ancreActive?.type ?? filtreAncre ?? undefined,
        ancreTrackingId: ancreActive?.id ?? undefined,
      });
      const zone = zoneRef.current;
      const hauteurAvant = zone ? zone.scrollHeight : 0;
      setMessages((precedents) => [...[...fil.items].reverse(), ...precedents]);
      setPage(suivante);
      requestAnimationFrame(() => {
        if (zone) zone.scrollTop = zone.scrollHeight - hauteurAvant;
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || t('filInterne.anciensImpossible'));
    } finally {
      setChargementAnciens(false);
    }
  };

  // ── Actions ──────────────────────────────────────────────────────────────
  const remplacer = (mis) => setMessages((liste) =>
    liste.map((m) => (m.messageTrackingId === mis.messageTrackingId ? mis : m)));

  const envoyer = async () => {
    const texte = corps.trim();
    if (!texte) return;
    setEnvoi(true);
    try {
      const cree = await discussionService.ecrire(entiteType, entiteId, {
        corps: texte,
        ancreType: ancreActive?.type,
        ancreTrackingId: ancreActive?.id,
        ancreLibelle: ancreActive?.libelle,
        reponseA: citation?.messageTrackingId,
      });
      // On ajoute la réponse du serveur plutôt que de tout recharger : le corps a été
      // réécrit côté serveur (noms de mention résolus), c'est cette version-là qui
      // doit s'afficher, et un rechargement complet ferait sauter la position de lecture.
      setMessages((precedents) => (cree ? [...precedents, cree] : precedents));
      setTotal((t) => t + 1);
      setCorps('');
      setCitation(null);
      requestAnimationFrame(versLeBas);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('filInterne.envoiImpossible'));
    } finally {
      setEnvoi(false);
    }
  };

  const reagir = async (message, embleme) => {
    try { remplacer(await discussionService.basculerReaction(message.messageTrackingId, embleme)); }
    catch (err) { toast.error(err?.response?.data?.message || t('filInterne.reactionImpossible')); }
  };

  const epingler = async (message, valeur) => {
    try {
      remplacer(await discussionService.epingler(message.messageTrackingId, valeur));
      charger({ silencieux: true });
    } catch (err) { toast.error(err?.response?.data?.message || t('filInterne.epinglageImpossible')); }
  };

  const modifier = async (message, texte) => {
    try { remplacer(await discussionService.modifier(message.messageTrackingId, texte)); }
    catch (err) { toast.error(err?.response?.data?.message || t('filInterne.modificationImpossible')); }
  };

  const supprimer = async (message) => {
    try { remplacer(await discussionService.supprimer(message.messageTrackingId)); }
    catch (err) { toast.error(err?.response?.data?.message || t('filInterne.suppressionImpossible')); }
  };

  const allerVers = (trackingId) => {
    const el = document.getElementById(`msg-${trackingId}`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    else toast.info(t('filInterne.messagePlusHaut'));
  };

  const restants = Math.max(total - messages.length, 0);

  const optionsAncre = useMemo(() => {
    const presentes = new Set((resume?.ancres || []).map((a) => a.ancreType));
    return [{ value: '', label: t('filInterne.toutLeDossier') },
    ...Object.entries(LIBELLES_ANCRE)
      .filter(([code]) => presentes.has(code))
      .map(([code, label]) => ({ value: code, label }))];
  }, [resume, t]);

  if (!peutLire) {
    return (
      <Alert
        type="info"
        showIcon
        message={t('filInterne.reserveeHabilitation')}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>

      {/* Bandeau de confidentialité - permanent, jamais une note en petit.
          C'est la seule chose qui empêche d'écrire ici ce qui devait aller à l'assuré. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
        background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7,
        fontSize: 11, color: '#475569', marginBottom: 8,
      }}>
        <LockOutlined />
        <span>{t('filInterne.confidentialite')}</span>
      </div>

      {/* Épinglés : la mémoire du dossier, lisible sans remonter le fil */}
      {resume?.epingles?.length > 0 && (
        <div style={{
          border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 8,
          padding: '7px 10px', marginBottom: 8,
        }}>
          {resume.epingles.map((e) => (
            <div
              key={e.messageTrackingId}
              onClick={() => allerVers(e.messageTrackingId)}
              style={{
                display: 'flex', gap: 6, alignItems: 'flex-start', cursor: 'pointer',
                fontSize: 12, color: '#78350f', lineHeight: 1.45,
              }}
            >
              <PushpinFilled style={{ fontSize: 11, marginTop: 3, flexShrink: 0, color: C.amber }} />
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <CorpsMessage corps={e.corps} mentions={e.mentions} moiTrackingId={moiTrackingId} />
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Filtre d'ancre - replier la conversation sur un élément du dossier */}
      {!ancreActive && optionsAncre.length > 1 && (
        <div style={{ marginBottom: 8 }}>
          <Select
            size="small"
            style={{ width: '100%' }}
            value={filtreAncre || ''}
            options={optionsAncre}
            onChange={(v) => setFiltreAncre(v || null)}
          />
        </div>
      )}

      {/* Ancre imposée par l'appelant */}
      {ancreActive && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 7,
          padding: '5px 9px', marginBottom: 8, fontSize: 11.5, color: '#3730a3',
        }}>
          <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {LIBELLES_ANCRE[ancreActive.type]} · {ancreActive.libelle}
          </span>
          <CloseOutlined style={{ cursor: 'pointer', fontSize: 11 }} onClick={() => onAncreChange?.(null)} />
        </div>
      )}

      {erreur && <Alert type="error" showIcon message={erreur} style={{ marginBottom: 8 }} />}

      {/* Fil */}
      <div
        ref={zoneRef}
        style={{
          flex: 1, minHeight: compact ? 180 : hauteur, maxHeight: hauteur, overflowY: 'auto',
          border: `1px solid ${C.border}`, borderRadius: 9, background: '#fff', padding: 8,
        }}
      >
        {chargement && <div style={{ textAlign: 'center', padding: 36 }}><Spin /></div>}

        {!chargement && !messages.length && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={ancreActive
              ? t('filInterne.aucunEchangeElement')
              : t('filInterne.personneNAEcrit')}
            style={{ marginTop: 40 }}
          />
        )}

        {!chargement && restants > 0 && (
          <div style={{ textAlign: 'center', marginBottom: 8 }}>
            <Button size="small" icon={<HistoryOutlined />} loading={chargementAnciens} onClick={chargerAnciens}>
              {t('filInterne.plusAnciens', { nombre: restants })}
            </Button>
          </div>
        )}

        {!chargement && messages.map((m) => (
          <BulleMessage
            key={m.messageTrackingId}
            message={m}
            moiTrackingId={moiTrackingId}
            peutEcrire={peutEcrire}
            surfligne={messageCible === m.messageTrackingId}
            surRepondre={setCitation}
            surReagir={reagir}
            surEpingler={epingler}
            surModifier={modifier}
            surSupprimer={supprimer}
            surAllerVers={allerVers}
          />
        ))}
      </div>

      {/* Saisie */}
      <div style={{ marginTop: 8 }}>
        {citation && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
            background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7,
            padding: '5px 9px', marginBottom: 6, fontSize: 11.5, color: C.muted,
          }}>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <Trans
                t={t}
                i18nKey="filInterne.enReponseA"
                values={{ nom: [citation.auteur?.prenoms, citation.auteur?.nom].filter(Boolean).join(' ') }}
                components={{ 1: <strong /> }}
              />
            </span>
            <CloseOutlined style={{ cursor: 'pointer', fontSize: 11 }} onClick={() => setCitation(null)} />
          </div>
        )}

        <SaisieMention
          entiteType={entiteType}
          entiteId={entiteId}
          valeur={corps}
          onChange={setCorps}
          onEnvoyer={envoyer}
          disabled={!peutEcrire || envoi}
          rows={compact ? 2 : 3}
          placeholder={peutEcrire
            ? t('filInterne.placeholderEcrire')
            : t('filInterne.ecrireRequiert')}
        />

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 7, gap: 8,
        }}>
          <Tooltip title={t('filInterne.entreeSauteLigne')}>
            <span style={{ fontSize: 10.5, color: C.faint }}>{t('filInterne.ctrlEntree')}</span>
          </Tooltip>
          <Button
            type="primary"
            size="small"
            icon={<SendOutlined />}
            loading={envoi}
            disabled={!peutEcrire || !corps.trim()}
            onClick={envoyer}
            // Style conditionnel : le vert forcé en ligne survivrait à l'état
            // désactivé et donnerait un bouton d'apparence active qui ne répond pas.
            style={!peutEcrire || !corps.trim() ? undefined : { background: C.greenMid, borderColor: C.greenMid }}
          >
            {t('filInterne.envoyer')}
          </Button>
        </div>
      </div>
    </div>
  );
}
